import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { crmService, Client } from '../services/crmService';
import { TargetIcon, BriefcaseIcon, CheckIcon, XMarkIcon, UserIcon, UsersIcon } from '../components/Icons';
import EmailComposeModal from '../components/EmailComposeModal';
import EnrollSequenceModal from '../components/sequences/EnrollSequenceModal';
import ContactEmailHistory from '../components/ContactEmailHistory';
import { OutboundEmailRow } from '../services/emailService';
import { clientStageBadgeClass } from '../utils/statusStyles';
import { ListToolbar } from '../components/crm/ListToolbar';
import { ListPageHeader } from '../components/crm/ListPageHeader';
import { DataListTable, Column } from '../components/crm/DataListTable';
import { DncBadge, dncRowClassName } from '../components/crm/DncBadge';
import { CrmEmptyState } from '../components/crm/EmptyState';
import { RowActionsMenu } from '../components/crm/RowActionsMenu';
import { CrmGridSkeleton } from '../components/crm/CrmGridSkeleton';
import { ViewLayoutToggle } from '../components/crm/ViewLayoutToggle';
import CsvImportWizard from '../components/crm/CsvImportWizard';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import { LifecycleStatusBadge } from '../components/crm/LifecycleStatusBadge';
import { ClientLifecycleStatus } from '../services/crmService';
import { getWebsiteDisplayText, getWebsiteLinkHref, sanitizeWebsiteUrlForSave } from '../utils/websiteUrl';
import ContactFormModal from '../components/crm/ContactFormModal';

function clientPlanOrService(c: Client): string {
  const cf = c.custom_fields || {};
  const raw = (cf.plan as string) || (cf.service as string) || (cf.engagement as string);
  if (raw) return raw;
  if (c.tags && c.tags.length > 0) return c.tags.slice(0, 2).join(', ');
  return '—';
}

const Clients: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientsLayout = searchParams.get('layout') === 'grid' ? 'grid' : 'list';
  const setClientsLayout = (mode: 'list' | 'grid') => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (mode === 'grid') n.set('layout', 'grid');
        else n.delete('layout');
        return n;
      },
      { replace: true }
    );
  };
  const [clients, setClients] = useState<Client[]>([]);
  const [clientCount, setClientCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCsvImportWizard, setShowCsvImportWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewPanel, setShowViewPanel] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editScheduledEmail, setEditScheduledEmail] = useState<OutboundEmailRow | null>(null);
  const [emailContact, setEmailContact] = useState<Client | null>(null);
  const [enrollContact, setEnrollContact] = useState<Client | null>(null);
  const [emailRefreshKey, setEmailRefreshKey] = useState(0);
  const [viewingClient, setViewingClient] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<ClientLifecycleStatus | 'all'>('active');
  const [ordering, setOrdering] = useState('-created_at');
  const [page, setPage] = useState(1);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        stage: 'client',
        ordering,
        page: String(page),
        lifecycle_status: lifecycleFilter,
      };
      if (searchTerm) params.search = searchTerm;

      const response = await crmService.getClients(params);
      setClients(response.results || []);
      setClientCount(response.count ?? response.results?.length ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, ordering, page, lifecycleFilter]);

  const handleCreateClient = async (clientData: any) => {
    try {
      await crmService.createClient({ ...clientData, stage: 'client' });
      await loadClients(); // Reload the client list
      setShowAddModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    }
  };

  const handleUpdateClient = async (clientId: string, clientData: any) => {
    try {
      await crmService.updateClient(clientId, { ...clientData, stage: 'client' });
      await loadClients();
      setShowEditModal(false);
      setSelectedClient(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update client');
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await crmService.deleteClient(clientId);
        await loadClients();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete client');
      }
    }
  };

  const matchesLifecycleFilter = useCallback(
    (status?: string | null) => lifecycleFilter === 'all' || (status || 'active') === lifecycleFilter,
    [lifecycleFilter]
  );

  const applyLifecycleStatus = async (
    client: Client,
    lifecycle_status: ClientLifecycleStatus,
    confirmText?: string
  ) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const updated = await crmService.updateClient(client.id, { lifecycle_status });
      setClients((prev) => {
        const next = prev.map((c) => (c.id === client.id ? updated : c));
        return next.filter((c) => matchesLifecycleFilter(c.lifecycle_status));
      });
      setClientCount((prev) => (matchesLifecycleFilter(updated.lifecycle_status) ? prev : Math.max(0, prev - 1)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lifecycle status');
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'lead':
        return <TargetIcon size={4} />;
      case 'prospect':
        return <BriefcaseIcon size={4} />;
      case 'client':
        return <CheckIcon size={4} />;
      case 'churned':
        return <XMarkIcon size={4} />;
      default:
        return <UserIcon size={4} />;
    }
  };

  const sortOptions = useMemo(
    () => [
      { value: '-created_at', label: 'Joined (newest)' },
      { value: 'created_at', label: 'Joined (oldest)' },
      { value: '-updated_at', label: 'Last activity' },
      { value: '-estimated_deal_value', label: 'Deal value (high)' },
      { value: 'full_name', label: 'Name A–Z' },
    ],
    []
  );

  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil((clientCount || 0) / PAGE_SIZE));

  const clientColumns: Column<Client>[] = [
    {
      id: 'name',
      header: 'Name',
      mobilePriority: 'primary',
      mobileLabel: 'Name',
      render: (c) => (
        <div>
          <div className="font-medium text-fg flex items-center gap-2 flex-wrap">
            <span>{c.full_name || c.display_name}</span>
            {c.do_not_contact ? <DncBadge /> : null}
          </div>
          <div className="text-xs text-fg-muted">{c.email}</div>
        </div>
      ),
    },
    {
      id: 'compliance',
      header: 'Reach',
      mobileLabel: 'Reach',
      className: 'w-[7rem]',
      cellClassName: 'align-top',
      render: (c) =>
        c.do_not_contact ? (
          <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Suppressed</span>
        ) : (
          <span className="text-xs text-fg-muted">Contactable</span>
        ),
    },
    { id: 'company', header: 'Company', mobileLabel: 'Company', render: (c) => c.company_name || '—' },
    {
      id: 'website',
      header: 'Website',
      render: (c) => {
        const href = getWebsiteLinkHref(c.website_url);
        const label = getWebsiteDisplayText(c.website_url) || '—';
        if (!href) return <span className="text-fg-muted">{label}</span>;
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            {label}
          </a>
        );
      },
    },
    {
      id: 'plan',
      header: 'Plan / tags',
      render: (c) => <span className="text-fg-secondary">{clientPlanOrService(c)}</span>,
    },
    {
      id: 'lifecycle',
      header: 'Status / Lifecycle',
      render: (c) => <LifecycleStatusBadge status={c.lifecycle_status} />,
    },
    {
      id: 'value',
      header: 'Est. deal value',
      render: (c) =>
        c.estimated_deal_value != null && c.estimated_deal_value !== ''
          ? `$${Number(c.estimated_deal_value).toLocaleString()}`
          : '—',
    },
    {
      id: 'monthly',
      header: 'Monthly value',
      render: (c) =>
        c.monthly_value != null && c.monthly_value !== ''
          ? `$${Number(c.monthly_value).toLocaleString()}`
          : '—',
    },
    {
      id: 'joined',
      header: 'Joined',
      render: (c) => new Date(c.created_at).toLocaleDateString(),
    },
    {
      id: 'activity',
      header: 'Last activity',
      render: (c) => new Date(c.updated_at).toLocaleString(),
    },
    {
      id: 'actions',
      header: '',
      className: 'w-24',
      cellClassName: 'text-right',
      render: (c) => (
        <RowActionsMenu
          items={[
            {
              id: 'view',
              label: 'View',
              onClick: () => {
                setViewingClient(c);
                setShowViewPanel(true);
              },
            },
            {
              id: 'edit',
              label: 'Edit',
              onClick: () => {
                setSelectedClient(c);
                setShowEditModal(true);
              },
            },
            {
              id: 'email',
              label: 'Send email',
              onClick: () => {
                setEmailContact(c);
                setShowEmailModal(true);
              },
            },
            { id: 'sequence', label: 'Start sequence', onClick: () => setEnrollContact(c) },
            { id: 'paused', label: 'Move to Paused', onClick: () => applyLifecycleStatus(c, 'paused') },
            {
              id: 'former',
              label: 'Mark Former',
              onClick: () => applyLifecycleStatus(c, 'former', 'Mark this client as former?'),
            },
            { id: 'restore', label: 'Restore to Active', onClick: () => applyLifecycleStatus(c, 'active') },
            { id: 'del', label: 'Delete', onClick: () => handleDeleteClient(c.id) },
          ]}
        />
      ),
    },
  ];

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Clients"
        subtitle="Active customers — move prospects here when a deal is won."
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowCsvImportWizard(true)}
              className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium"
            >
              Import CSV
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="btn-primary px-4 py-2 rounded-xl text-sm font-medium"
            >
              Add client
            </button>
            <Link to="/prospects" className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium">
              Prospects
            </Link>
            <Link to="/pipeline/upsells" className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium">
              Upsells
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-2">
        <ListToolbar
          searchValue={searchTerm}
          onSearchChange={(v) => {
            setSearchTerm(v);
            setPage(1);
          }}
          searchPlaceholder="Search name, company, email…"
          sortValue={ordering}
          sortOptions={sortOptions}
          onSortChange={(v) => {
            setOrdering(v);
            setPage(1);
          }}
          showClear={!!(searchTerm || lifecycleFilter !== 'active')}
          onClearFilters={() => {
            setSearchTerm('');
            setOrdering('-created_at');
            setLifecycleFilter('active');
            setPage(1);
          }}
          advancedFilterCount={lifecycleFilter !== 'active' ? 1 : 0}
          advancedFilters={
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-fg-muted">Status / Lifecycle</label>
              <select
                value={lifecycleFilter}
                onChange={(e) => {
                  setLifecycleFilter(e.target.value as ClientLifecycleStatus | 'all');
                  setPage(1);
                }}
                className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[12rem]"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="former">Former</option>
                <option value="all">All</option>
              </select>
            </div>
          }
        />
        <div className="flex justify-end">
          <ViewLayoutToggle mode={clientsLayout} onChange={setClientsLayout} />
        </div>
      </div>

      {error && (
        <div className="alert-danger rounded-xl px-4 py-3">{error}</div>
      )}

      {clientsLayout === 'list' ? (
        <DataListTable<Client>
          columns={clientColumns}
          rows={clients}
          getRowKey={(c) => c.id}
          onRowClick={(c) => {
            setViewingClient(c);
            setShowViewPanel(true);
          }}
          getRowClassName={(c) => dncRowClassName(c.do_not_contact)}
          loading={loading}
          skeletonColumns={10}
          emptyContent={
            <CrmEmptyState
              title="No clients yet"
              description={
                lifecycleFilter === 'active'
                  ? 'Clients are active customers. When a prospect closes, move them here so value and follow-ups stay visible.'
                  : 'No clients match the selected lifecycle filter.'
              }
              actionLabel="Add a client"
              onAction={() => setShowAddModal(true)}
              secondaryHint="You can also mark a prospect as “client” from the Prospects list or a prospect’s profile."
            />
          }
        />
      ) : loading ? (
        <CrmGridSkeleton />
      ) : clients.length === 0 ? (
        <CrmEmptyState
          title="No clients yet"
          description={
            lifecycleFilter === 'active'
              ? 'Clients are active customers. When a prospect closes, move them here so value and follow-ups stay visible.'
              : 'No clients match the selected lifecycle filter.'
          }
          actionLabel="Add a client"
          onAction={() => setShowAddModal(true)}
          secondaryHint="You can also mark a prospect as “client” from the Prospects list or a prospect’s profile."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {clients.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-border bg-surface p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                setViewingClient(c);
                setShowViewPanel(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                  e.preventDefault();
                  setViewingClient(c);
                  setShowViewPanel(true);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-fg">{c.full_name || c.display_name}</div>
                  <div className="truncate text-xs text-fg-muted">{c.email}</div>
                </div>
                <div data-row-action className="shrink-0">
                  <RowActionsMenu
                    items={[
                      {
                        id: 'view',
                        label: 'View',
                        onClick: () => {
                          setViewingClient(c);
                          setShowViewPanel(true);
                        },
                      },
                      {
                        id: 'edit',
                        label: 'Edit',
                        onClick: () => {
                          setSelectedClient(c);
                          setShowEditModal(true);
                        },
                      },
                      {
                        id: 'email',
                        label: 'Send email',
                        onClick: () => {
                          setEmailContact(c);
                          setShowEmailModal(true);
                        },
                      },
                      { id: 'paused', label: 'Move to Paused', onClick: () => applyLifecycleStatus(c, 'paused') },
                      {
                        id: 'former',
                        label: 'Mark Former',
                        onClick: () => applyLifecycleStatus(c, 'former', 'Mark this client as former?'),
                      },
                      { id: 'restore', label: 'Restore to Active', onClick: () => applyLifecycleStatus(c, 'active') },
                      { id: 'del', label: 'Delete', onClick: () => handleDeleteClient(c.id) },
                    ]}
                  />
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <dt className="text-fg-muted">Company</dt>
                <dd className="text-fg">{c.company_name || '—'}</dd>
                <dt className="text-fg-muted">Website</dt>
                <dd className="text-fg">
                  {(() => {
                    const href = getWebsiteLinkHref(c.website_url);
                    const label = getWebsiteDisplayText(c.website_url) || '—';
                    if (!href) return <span className="text-fg-muted">{label}</span>;
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {label}
                      </a>
                    );
                  })()}
                </dd>
                <dt className="text-fg-muted">Plan / tags</dt>
                <dd className="text-fg-secondary">{clientPlanOrService(c)}</dd>
                <dt className="text-fg-muted">Lifecycle</dt>
                <dd className="text-fg">
                  <LifecycleStatusBadge status={c.lifecycle_status} />
                </dd>
                <dt className="text-fg-muted">Est. value</dt>
                <dd className="text-fg">
                  {c.estimated_deal_value != null && c.estimated_deal_value !== ''
                    ? `$${Number(c.estimated_deal_value).toLocaleString()}`
                    : '—'}
                </dd>
                <dt className="text-fg-muted">Joined</dt>
                <dd className="text-fg">{new Date(c.created_at).toLocaleDateString()}</dd>
                <dt className="text-fg-muted">Last activity</dt>
                <dd className="text-fg">{new Date(c.updated_at).toLocaleString()}</dd>
              </dl>
            </div>
          ))}
        </div>
      )}
      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-4 text-sm text-fg-muted">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      {/* Add Client Modal */}
      {showAddModal && (
        <ClientModal
          onSave={handleCreateClient}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showCsvImportWizard && (
        <CsvImportWizard
          open={showCsvImportWizard}
          onClose={() => setShowCsvImportWizard(false)}
          defaultObjectType="client"
          lockObjectType
          onImported={() => loadClients()}
        />
      )}

      {/* Edit Client Modal */}
      {showEditModal && selectedClient && (
        <EditClientModal
          client={selectedClient}
          onSave={(clientData) => handleUpdateClient(selectedClient.id, clientData)}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClient(null);
          }}
        />
      )}

      {/* View Client Panel (read-only) */}
      {showViewPanel && viewingClient && (
        <ClientViewPanel
          client={viewingClient}
          onClose={() => {
            setShowViewPanel(false);
            setViewingClient(null);
            setShowEmailModal(false);
          }}
          onEdit={() => {
            setSelectedClient(viewingClient);
            setShowViewPanel(false);
            setShowEditModal(true);
            setViewingClient(null);
            setShowEmailModal(false);
          }}
          getStageIcon={getStageIcon}
          onOpenEmail={() => {
            setEmailContact(viewingClient);
            setEditScheduledEmail(null);
            setShowEmailModal(true);
          }}
          onEditScheduled={(row) => {
            setEmailContact(viewingClient);
            setEditScheduledEmail(row);
            setShowEmailModal(true);
          }}
          emailRefreshKey={emailRefreshKey}
        />
      )}

      {showEmailModal && emailContact && (
        <EmailComposeModal
          open={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEditScheduledEmail(null);
            setEmailContact(null);
          }}
          contact={emailContact}
          contactKind="client"
          followUpContext="client"
          editScheduledEmail={editScheduledEmail}
          onSent={() => setEmailRefreshKey((k) => k + 1)}
        />
      )}

      {enrollContact && (
        <EnrollSequenceModal
          contact={{
            type: 'client',
            id: enrollContact.id,
            name: enrollContact.full_name || enrollContact.display_name || enrollContact.email,
          }}
          onClose={() => setEnrollContact(null)}
        />
      )}
    </div>
  );
};

interface ClientModalProps {
  onSave: (client: any) => void;
  onClose: () => void;
}

const ClientModal: React.FC<ClientModalProps> = ({ onSave, onClose }) => (
  <ContactFormModal
    title="Add client"
    submitLabel="Add client"
    mode="client"
    values={{
      full_name: '',
      email: '',
      phone: '',
      company_name: '',
      website_url: '',
      job_title: '',
      source: 'Direct',
      stage: 'client',
      tags: [],
      notes: '',
      address: '',
      lifecycle_status: 'active' as ClientLifecycleStatus,
    }}
    onClose={onClose}
    onSubmit={(payload) => onSave({ ...payload, website_url: sanitizeWebsiteUrlForSave(payload.website_url) })}
  />
);

interface EditClientModalProps {
  client: Client;
  onSave: (clientData: any) => void;
  onClose: () => void;
}

const EditClientModal: React.FC<EditClientModalProps> = ({ client, onSave, onClose }) => (
  <ContactFormModal
    title="Edit client"
    submitLabel="Save changes"
    mode="client"
    values={{
      title: client.title || '',
      full_name: client.full_name || '',
      email: client.email || '',
      phone: client.phone || '',
      company_name: client.company_name || '',
      website_url: client.website_url || '',
      job_title: client.job_title || '',
      source: client.source || 'Direct',
      notes: client.notes || '',
      address: client.address || '',
      lifecycle_status: (client.lifecycle_status as ClientLifecycleStatus) || 'active',
      stage: (client.stage as 'client' | 'churned') || 'client',
      tags: client.tags || [],
    }}
    onClose={onClose}
    onSubmit={(payload) => onSave({ ...payload, website_url: sanitizeWebsiteUrlForSave(payload.website_url) })}
  />
);

// View Client Panel (read-only)
interface ClientViewPanelProps {
  client: Client;
  onClose: () => void;
  onEdit: () => void;
  getStageIcon: (stage: string) => React.ReactNode;
  onOpenEmail: () => void;
  onEditScheduled: (row: OutboundEmailRow) => void;
  emailRefreshKey: number;
}

const ClientViewPanel: React.FC<ClientViewPanelProps> = ({ client, onClose, onEdit, getStageIcon, onOpenEmail, onEditScheduled, emailRefreshKey }) => {
  return (
    <div className="modal-backdrop">
      <div className="relative top-10 mx-auto p-5 border border-border w-full max-w-2xl shadow-lg rounded-2xl bg-surface">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-fg">Client Details</h3>
            <button
              onClick={onClose}
              className="text-fg-muted hover:text-fg-secondary"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-primary-dark rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xl">
                  {client.full_name?.[0] || client.email?.[0] || '?'}
                </span>
              </div>
              <div className="ml-4">
                <h2 className="text-xl font-bold text-fg">{client.full_name || client.display_name}</h2>
                <p className="text-fg-muted">{client.email}</p>
                <span className={`inline-flex items-center mt-2 ${clientStageBadgeClass(client.stage)}`}>
                  <span className="mr-1">{getStageIcon(client.stage)}</span>
                  {client.stage}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface-muted/20 p-4">
              <h4 className="text-sm font-semibold text-fg mb-3">Business Identity</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Phone</label>
                <p className="text-fg">{formatPhoneDisplay(client.phone)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Company</label>
                <p className="text-fg">{client.company_name || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Website</label>
                {(() => {
                  const href = getWebsiteLinkHref(client.website_url);
                  const label = getWebsiteDisplayText(client.website_url) || '—';
                  if (!href) return <p className="text-fg-muted">{label}</p>;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {label}
                    </a>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Job Title</label>
                <p className="text-fg">{client.job_title || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Source</label>
                <p className="text-fg">{client.source || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Added</label>
                <p className="text-fg">{new Date(client.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            </div>

            {client.address && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-1">Address</label>
                <p className="text-fg whitespace-pre-wrap">{client.address}</p>
              </div>
            )}

            {client.notes && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-1">Notes</label>
                <p className="text-fg whitespace-pre-wrap">{client.notes}</p>
              </div>
            )}

            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-semibold text-fg mb-2">Email</h4>
              <ContactEmailHistory clientId={client.id} refreshKey={emailRefreshKey} onEditScheduled={onEditScheduled} />
              <button
                type="button"
                onClick={onOpenEmail}
                className="mt-3 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-xl"
              >
                Send follow-up
              </button>
            </div>

            {client.tags && client.tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-1">Tags</label>
                <div className="flex flex-wrap gap-1">
                  {client.tags.map((tag, index) => (
                    <span key={index} className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-surface-muted text-fg">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <button
                onClick={onClose}
                className="px-4 py-2 btn-surface-muted"
              >
                Close
              </button>
              <button
                onClick={onEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary rounded-xl"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clients;